import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';
import ExportButton from '../../../components/ExportButton';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { stripHtml as sanitizeStripHtml } from '../../../utils/sanitize';
import {
  PulseReportTicket,
  PulseReportResponse,
  PulseReportBaseFilters,
  PulseReportChartFilters,
  ColumnConfig,
  ColumnGroupConfig,
  TicketType,
  priorityLabels,
  priorityColors,
  stateLabels,
  changeStateLabels,
  problemStateLabels,
  ticketTypeLabels,
  channelLabels,
  slaStatusLabels,
  slaStatusColors,
  closureCodeLabels,
} from '../types/pulseReport';

interface UnifiedPulseReportTableProps {
  visible: boolean;
  filterLabel: string;
  baseFilters: PulseReportBaseFilters;
  chartFilters: PulseReportChartFilters;
  onClose: () => void;
  ticketType?: TicketType;
  customerName?: string;
}

// Format duration (minutes) for display
const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

// Format percentage for display
const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${Math.round(value)}%`;
};

// Strip HTML tags from text
// SECURITY: Uses DOMPurify-based stripHtml for safe HTML removal
const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '-';
  const result = sanitizeStripHtml(html);
  return result || '-';
};

// Priority badge component
const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const label = priorityLabels[priority] || priority;
  const colorClass = priorityColors[priority] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

// State badge component
const StateBadge: React.FC<{ state: string; ticketType: TicketType }> = ({ state, ticketType }) => {
  let label = state;

  if (ticketType === 'PROBLEM') {
    label = problemStateLabels[state] || stateLabels[state] || state;
  } else if (ticketType === 'CHANGE') {
    label = changeStateLabels[state] || stateLabels[state] || state;
  } else {
    label = stateLabels[state] || state;
  }

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {label}
    </span>
  );
};

// SLA Status badge component with Breached/Met/In Progress coloring
const SLAStatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status || status === 'N/A') return <span className="text-gray-400 text-xs">-</span>;

  const label = slaStatusLabels[status] || status;
  const colorClass = slaStatusColors[status] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${colorClass}`}>
      {label}
    </span>
  );
};

const UnifiedPulseReportTable: React.FC<UnifiedPulseReportTableProps> = ({
  visible,
  filterLabel,
  baseFilters,
  chartFilters,
  onClose,
  ticketType,
  customerName,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const { formatDateTime: formatDateTimeBase } = useDateFormatter();

  // Wrapper to match column formatter signature
  const formatDateTime = useCallback((value: string | null | undefined) => formatDateTimeBase(value), [formatDateTimeBase]);

  // Column definitions - all 39 columns
  const allColumns: ColumnConfig[] = useMemo(() => [
    // Column 1: Task Type
    {
      key: 'taskType',
      header: 'Task Type',
      width: 'w-32',
      minWidth: 'min-w-[128px]',
      formatter: (value: string) => ticketTypeLabels[value] || value,
    },
    // Column 2: Number (sticky)
    {
      key: 'number',
      header: 'Number',
      width: 'w-32',
      minWidth: 'min-w-[128px]',
      sticky: true,
      stickyOffset: 'left-0',
      formatter: (value: string) => (
        <span className="font-medium text-blue-600">{value}</span>
      ),
    },
    // Column 3: Short Description (sticky)
    {
      key: 'shortDescription',
      header: 'Short Description',
      width: 'w-64',
      minWidth: 'min-w-[256px]',
      sticky: true,
      stickyOffset: 'left-[128px]',
      formatter: (value: string) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs" title={cleanValue}>
            {cleanValue}
          </div>
        );
      },
    },
    // Column 4: Type
    {
      key: 'type',
      header: 'Type',
      width: 'w-32',
      minWidth: 'min-w-[128px]',
      formatter: (value: string) => value || '-',
    },
    // Column 6: Priority
    {
      key: 'priority',
      header: 'Priority',
      width: 'w-32',
      minWidth: 'min-w-[128px]',
      formatter: (value: string) => <PriorityBadge priority={value} />,
    },
    // Column 7: State
    {
      key: 'state',
      header: 'State',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string, row: PulseReportTicket) => (
        <StateBadge state={value} ticketType={row.ticketType} />
      ),
    },
    // Column 8: Assignment Group
    {
      key: 'assignmentGroup',
      header: 'Assignment Group',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 9: Assigned To
    {
      key: 'assignedTo',
      header: 'Assigned To',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 10: Company
    {
      key: 'company',
      header: 'Company',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 11: Customer Contact
    {
      key: 'customerContact',
      header: 'Customer Contact',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Response SLA Group (Start, Target, Status, Breached At, Reason)
    {
      key: 'responseSlaStart',
      header: 'Start',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'response',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'responseSlaTarget',
      header: 'Target',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'response',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'responseSlaStatus',
      header: 'Status',
      width: 'w-24',
      minWidth: 'min-w-[96px]',
      groupId: 'response',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => <SLAStatusBadge status={value} />,
    },
    {
      key: 'responseSlaBreachedAt',
      header: 'Breached At',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'response',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'responseSlaBreachReason',
      header: 'Reason',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      groupId: 'response',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs text-red-600" title={cleanValue}>
            {cleanValue}
          </div>
        );
      },
    },
    // Ongoing SLA Group (Start, Target, Status, Breached At, Reason)
    {
      key: 'ongoingSlaStart',
      header: 'Start',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'ongoing',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'ongoingSlaTarget',
      header: 'Target',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'ongoing',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'ongoingSlaStatus',
      header: 'Status',
      width: 'w-24',
      minWidth: 'min-w-[96px]',
      groupId: 'ongoing',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => <SLAStatusBadge status={value} />,
    },
    {
      key: 'ongoingSlaBreachedAt',
      header: 'Breached At',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'ongoing',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'ongoingSlaBreachReason',
      header: 'Reason',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      groupId: 'ongoing',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs text-red-600" title={cleanValue}>
            {cleanValue}
          </div>
        );
      },
    },
    // Resolution SLA Group (Start, Target, Status, Breached At, Reason)
    {
      key: 'resolutionSlaStart',
      header: 'Start',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'resolution',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'resolutionSlaTarget',
      header: 'Target',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'resolution',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'resolutionSlaStatus',
      header: 'Status',
      width: 'w-24',
      minWidth: 'min-w-[96px]',
      groupId: 'resolution',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => <SLAStatusBadge status={value} />,
    },
    {
      key: 'resolutionSlaBreachedAt',
      header: 'Breached At',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      groupId: 'resolution',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: formatDateTime,
    },
    {
      key: 'resolutionSlaBreachReason',
      header: 'Reason',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      groupId: 'resolution',
      isGroupChild: true,
      hideFor: ['CHANGE', 'PROBLEM'],
      formatter: (value: string | null) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs text-red-600" title={cleanValue}>
            {cleanValue}
          </div>
        );
      },
    },
    // Column 19: Created By
    {
      key: 'createdBy',
      header: 'Created By',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 20: Last Updated Time
    {
      key: 'ticketLastUpdatedTime',
      header: 'Last Updated Time',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      formatter: formatDateTime,
    },
    // Column 21: Last Updated By
    {
      key: 'lastUpdatedBy',
      header: 'Last Updated By',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 22: Channel
    {
      key: 'channel',
      header: 'Channel',
      width: 'w-32',
      minWidth: 'min-w-[128px]',
      formatter: (value: string | null) => (value ? channelLabels[value] || value : '-'),
    },
    // Column 23: Resource
    {
      key: 'resource',
      header: 'Resource',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 24: Ticket Created
    {
      key: 'ticketCreated',
      header: 'Ticket Created',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      formatter: formatDateTime,
    },
    // Column 25: Ticket Resolved
    {
      key: 'ticketResolved',
      header: 'Ticket Resolved',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      formatter: formatDateTime,
    },
    // Column 26: Resolved By
    {
      key: 'ticketResolvedBy',
      header: 'Resolved By',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 27: Ticket Closed
    {
      key: 'ticketClosed',
      header: 'Ticket Closed',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      formatter: formatDateTime,
    },
    // Column 28: Closed By
    {
      key: 'ticketClosedBy',
      header: 'Closed By',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      formatter: (value: string | null) => value || '-',
    },
    // Column 29: On Hold Reason
    {
      key: 'onHoldReason',
      header: 'On Hold Reason',
      width: 'w-48',
      minWidth: 'min-w-[192px]',
      formatter: (value: string | null) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs" title={cleanValue !== '-' ? cleanValue : ''}>
            {cleanValue}
          </div>
        );
      },
    },
    // Column 30: Closure Remarks
    {
      key: 'ticketClosureRemarks',
      header: 'Closure Remarks',
      width: 'w-48',
      minWidth: 'min-w-[192px]',
      formatter: (value: string | null) => {
        const cleanValue = stripHtml(value);
        return (
          <div className="truncate max-w-xs" title={cleanValue !== '-' ? cleanValue : ''}>
            {cleanValue}
          </div>
        );
      },
    },
    // Column 31: Ticket Duration (Mins)
    {
      key: 'ticketDurationMins',
      header: 'Ticket Duration (Mins)',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      formatter: formatDuration,
    },
    // Column 32: Change Status (Change only)
    {
      key: 'changeStatus',
      header: 'Change Status',
      width: 'w-36',
      minWidth: 'min-w-[144px]',
      hideFor: ['INCIDENT', 'PROBLEM', 'SERVICE_REQUEST', 'CASE'],
      formatter: (value: string | null) => (value ? changeStateLabels[value] || value : '-'),
    },
    // Column 33: Planned Start Date (Change only)
    {
      key: 'plannedStartDate',
      header: 'Planned Start Date',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      hideFor: ['INCIDENT', 'PROBLEM', 'SERVICE_REQUEST', 'CASE'],
      formatter: formatDateTime,
    },
    // Column 34: Planned End Date (Change only)
    {
      key: 'plannedEndDate',
      header: 'Planned End Date',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      hideFor: ['INCIDENT', 'PROBLEM', 'SERVICE_REQUEST', 'CASE'],
      formatter: formatDateTime,
    },
    // Column 35: Actual Start Date (Change only)
    {
      key: 'actualStartDate',
      header: 'Actual Start Date',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      hideFor: ['INCIDENT', 'PROBLEM', 'SERVICE_REQUEST', 'CASE'],
      formatter: formatDateTime,
    },
    // Column 36: Actual End Date (Change only)
    {
      key: 'actualEndDate',
      header: 'Actual End Date',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      hideFor: ['INCIDENT', 'PROBLEM', 'SERVICE_REQUEST', 'CASE'],
      formatter: formatDateTime,
    },
    // Column 37: Closure Code (not applicable for Incidents and Service Requests)
    {
      key: 'closureCode',
      header: 'Closure Code',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      hideFor: ['INCIDENT', 'SERVICE_REQUEST'],
      formatter: (value: string | null) => (value ? closureCodeLabels[value] || value : '-'),
    },
    // Column 38: RCA Presented By (Problem only)
    {
      key: 'rcaPresentedBy',
      header: 'RCA Presented By',
      width: 'w-40',
      minWidth: 'min-w-[160px]',
      hideFor: ['INCIDENT', 'CHANGE', 'SERVICE_REQUEST', 'CASE'],
      formatter: (value: string | null) => value || '-',
    },
    // Column 39: Problem Date (Problem only)
    {
      key: 'problemDate',
      header: 'Problem Date',
      width: 'w-44',
      minWidth: 'min-w-[176px]',
      hideFor: ['INCIDENT', 'CHANGE', 'SERVICE_REQUEST', 'CASE'],
      formatter: formatDateTime,
    },
  ], [formatDateTime]);

  // Filter visible columns based on ticketType
  const visibleColumns = useMemo(() => {
    if (!ticketType) {
      // Show all columns when no specific ticket type filter
      return allColumns;
    }

    return allColumns.filter(col => {
      // Check hideFor condition
      if (col.hideFor && col.hideFor.includes(ticketType)) {
        return false;
      }
      // Check showFor condition
      if (col.showFor && !col.showFor.includes(ticketType)) {
        return false;
      }
      return true;
    });
  }, [allColumns, ticketType]);

  // Build query parameters
  const buildQueryParams = () => {
    const params: Record<string, string> = {
      page: String(page),
      limit: String(limit),
    };

    // Add base filters
    if (baseFilters.customerId) params.customerId = baseFilters.customerId;
    if (baseFilters.timeRange) params.timeRange = baseFilters.timeRange;
    if (baseFilters.assignmentGroupId) params.assignmentGroupId = baseFilters.assignmentGroupId;
    if (baseFilters.timezoneOffset !== undefined) params.timezoneOffset = String(baseFilters.timezoneOffset);

    // Add chart filters
    if (chartFilters.ticketType) params.ticketType = chartFilters.ticketType;
    if (chartFilters.state) params.state = chartFilters.state;
    if (chartFilters.priority) params.priority = chartFilters.priority;
    if (chartFilters.slaBreached) params.slaBreached = chartFilters.slaBreached;
    if (chartFilters.filterType) params.filterType = chartFilters.filterType;
    if (chartFilters.filterValue) params.filterValue = chartFilters.filterValue;
    if (chartFilters.date) params.date = chartFilters.date;
    if (chartFilters.agingBucket) params.agingBucket = chartFilters.agingBucket;
    if (chartFilters.problemState) params.problemState = chartFilters.problemState;
    if (chartFilters.changeState) params.changeState = chartFilters.changeState;
    if (chartFilters.changeType) params.changeType = chartFilters.changeType;

    return params;
  };

  // Fetch tickets
  const { data, isLoading, isError, error } = useQuery<PulseReportResponse>({
    queryKey: ['pulseReportTickets', page, limit, baseFilters, chartFilters],
    queryFn: async () => {
      const params = buildQueryParams();
      const response = await api.get('/dashboard/pulse-report/tickets', { params });
      return response.data;
    },
    enabled: visible,
  });

  // Handle row click - open ticket detail in new tab so dashboard stays visible
  const handleRowClick = (ticket: PulseReportTicket, event: React.MouseEvent) => {
    const routes: Record<TicketType, string> = {
      INCIDENT: `/incidents/${ticket.id}`,
      PROBLEM: `/problems/${ticket.id}`,
      CHANGE: `/changes/${ticket.id}`,
      SERVICE_REQUEST: `/service-requests/${ticket.id}`,
      CASE: `/cases/${ticket.id}`,
    };

    const route = routes[ticket.ticketType];
    if (route) {
      // Open in new tab so user can come back to dashboard with table still visible
      // Ctrl/Cmd+Click also opens in new tab (browser default)
      if (event.ctrlKey || event.metaKey) {
        window.open(route, '_blank');
      } else {
        // Default: open in new tab
        window.open(route, '_blank');
      }
    }
  };

  // Build export filters for background export
  const exportFilters = useMemo(() => ({
    ...baseFilters,
    ...chartFilters,
  }), [baseFilters, chartFilters]);

  // Handle page size change
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to first page
  };

  if (!visible) return null;

  return (
    <div className="bg-white rounded-lg shadow mt-6">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Showing: {filterLabel}
            {data?.total ? ` (${data.total} tickets)` : ''}
          </h3>
          {customerName && customerName !== 'All Customers' && (
            <p className="text-sm text-gray-500">Customer: {customerName}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data?.tickets && data.tickets.length > 0 && (
            <ExportButton
              exportType="PULSE_REPORT"
              filters={exportFilters}
              totalRows={data.total}
              disabled={!data?.tickets || data.tickets.length === 0}
            />
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-sm text-gray-500">Loading tickets...</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="p-4 bg-red-50 text-red-700">
          Failed to load tickets: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {/* Table */}
      {data && !isLoading && (
        <>
          <div className="overflow-x-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              {/* Header - Two-row structure for grouped SLA columns */}
              <thead className="bg-gray-50 sticky top-0 z-20">
                {/* First row: Group headers + non-grouped columns with rowSpan */}
                <tr className="border-b border-gray-200">
                  {(() => {
                    // Calculate group headers and non-grouped columns
                    const groupedColumns: { groupId: string; header: string; colSpan: number; bgColor: string }[] = [
                      { groupId: 'response', header: 'Response SLA', colSpan: 5, bgColor: 'bg-blue-50' },
                      { groupId: 'ongoing', header: 'Ongoing SLA', colSpan: 5, bgColor: 'bg-purple-50' },
                      { groupId: 'resolution', header: 'Resolution SLA', colSpan: 5, bgColor: 'bg-green-50' },
                    ];
                    let currentGroupId: string | null = null;
                    const elements: React.ReactNode[] = [];

                    visibleColumns.forEach((col, idx) => {
                      if (col.isGroupChild && col.groupId) {
                        // Only render group header when we encounter first column of a group
                        if (currentGroupId !== col.groupId) {
                          currentGroupId = col.groupId;
                          const groupConfig = groupedColumns.find(g => g.groupId === col.groupId);
                          if (groupConfig) {
                            elements.push(
                              <th
                                key={`group-${col.groupId}`}
                                colSpan={groupConfig.colSpan}
                                className={`px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap ${groupConfig.bgColor} border-l border-r border-gray-200`}
                              >
                                {groupConfig.header}
                              </th>
                            );
                          }
                        }
                      } else {
                        currentGroupId = null;
                        elements.push(
                          <th
                            key={col.key}
                            rowSpan={2}
                            className={`
                              px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap align-middle
                              ${col.sticky ? `sticky ${col.stickyOffset} bg-gray-50 z-10` : ''}
                              ${col.sticky && idx === 1 ? 'shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''}
                            `}
                          >
                            {col.header}
                          </th>
                        );
                      }
                    });
                    return elements;
                  })()}
                </tr>
                {/* Second row: Child headers for grouped columns */}
                <tr>
                  {visibleColumns.filter(col => col.isGroupChild).map((col) => {
                    const groupBgColors: Record<string, string> = {
                      response: 'bg-blue-50',
                      ongoing: 'bg-purple-50',
                      resolution: 'bg-green-50',
                    };
                    const bgColor = col.groupId ? groupBgColors[col.groupId] || 'bg-gray-50' : 'bg-gray-50';
                    return (
                      <th
                        key={col.key}
                        className={`px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${bgColor} border-b border-gray-200`}
                      >
                        {col.header}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* Body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {data.tickets.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="px-4 py-8 text-center text-sm text-gray-500">
                      No tickets found matching the filter criteria.
                    </td>
                  </tr>
                ) : (
                  data.tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={(e) => handleRowClick(ticket, e)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      {visibleColumns.map((col, idx) => {
                        const value = ticket[col.key as keyof PulseReportTicket];
                        const formattedValue = col.formatter
                          ? col.formatter(value, ticket)
                          : value ?? '-';

                        return (
                          <td
                            key={col.key}
                            className={`
                              px-4 py-3 text-sm text-gray-900 whitespace-nowrap
                              ${col.sticky ? `sticky ${col.stickyOffset} bg-white z-10` : ''}
                              ${col.sticky && idx === 2 ? 'shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' : ''}
                            `}
                          >
                            {formattedValue}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-2 bg-gray-50">
            {data.total > 0 ? (
              <>
                {/* Left: Records per page dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={limit}
                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {[25, 50, 100, 200].map((size, index) => {
                      const previousSize = index > 0 ? [25, 50, 100, 200][index - 1] : 0;
                      const isDisabled = previousSize >= data.total && size > data.total;
                      return (
                        <option key={size} value={size} disabled={isDisabled}>
                          {size}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                {/* Center: Entry count */}
                <div className="text-sm text-gray-700">
                  Showing {Math.min((page - 1) * limit + 1, data.total)} to {Math.min(page * limit, data.total)} of {data.total} entries
                </div>

                {/* Right: Pagination buttons */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="First page"
                  >
                    &laquo;
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const totalPages = data.totalPages;
                    const pages: (number | string)[] = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (page <= 3) {
                        pages.push(1, 2, 3, 4, '...', totalPages);
                      } else if (page >= totalPages - 2) {
                        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                      } else {
                        pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                      }
                    }

                    return pages.map((p, idx) => (
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className={`px-3 py-1 text-sm border rounded-md ${
                            page === p
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    ));
                  })()}

                  <button
                    onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                    disabled={page >= data.totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(data.totalPages)}
                    disabled={page >= data.totalPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Last page"
                  >
                    &raquo;
                  </button>
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-500 w-full text-center">No records</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UnifiedPulseReportTable;
